import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Tag,
  Space,
  message,
  Empty,
  Spin,
  Popconfirm,
  Modal,
  Checkbox
} from 'antd'
import {
  FileProtectOutlined,
  SendOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SolutionOutlined,
  LinkOutlined
} from '@ant-design/icons'
import ConsentAnswerList from './ConsentAnswerList'
import styled from 'styled-components'
import ConsentSignLinkModal from './ConsentSignLinkModal'
import { useMembership } from '../hooks/useMembership'
import { getApiErrorMessage } from '../utils/api'
import { API_BASE_URL } from '../utils/apiutils'
import {
  listConsentFormsByBooking,
  getConsentFormDetail,
  listConsentFormTemplates,
  sendConsentForm,
  resendConsentForm,
  getConsentSignLink,
  consentFormPrintableUrl,
  CONSENT_STATUS_META
} from '../api/consentForm'
import { formatTz } from '../utils/timezone'

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 20px 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  border: 1px solid #f0f0f0;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1d1d1f;
  margin-bottom: 14px;

  .anticon {
    color: #5b8def;
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #f5f5f7;

  &:last-child {
    border-bottom: none;
  }
`

const Info = styled.div`
  min-width: 0;
  .name {
    font-weight: 600;
    color: #1d1d1f;
  }
  .meta {
    font-size: 12px;
    color: #86868b;
    margin-top: 2px;
  }
`

const statusTag = (status) => {
  const meta = CONSENT_STATUS_META[status] || { color: 'default', label: status }
  return <Tag color={meta.color}>{meta.label}</Tag>
}

/**
 * 預約詳情內的「施作同意書」區塊：顯示此預約的同意書狀態，並可手動發送 / 重發 / 開啟留存。
 * 未開通同意書模組（方案不符）時不顯示。
 */
const ConsentFormBookingSection = ({ businessId, bookingId }) => {
  const { hasFeature } = useMembership()
  const featureEnabled = hasFeature('consentForm')
  // 問卷作答：點開才抓詳情，清單本身不背這份資料
  const [answerView, setAnswerView] = useState({
    open: false,
    loading: false,
    title: '',
    answers: []
  })

  const openAnswers = async (row) => {
    setAnswerView({
      open: true,
      loading: true,
      title: row.templateTitle,
      answers: []
    })
    try {
      const res = await getConsentFormDetail(businessId, row._id)
      setAnswerView((prev) => ({
        ...prev,
        loading: false,
        answers: res?.data?.data?.signature?.answers || []
      }))
    } catch (err) {
      setAnswerView((prev) => ({ ...prev, open: false, loading: false }))
      message.error(getApiErrorMessage(err, '讀取作答失敗'))
    }
  }

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [sending, setSending] = useState(false)
  // 發送前的範本挑選：{ open, loading, templates, selected }
  const [picker, setPicker] = useState({
    open: false,
    loading: false,
    templates: [],
    selected: []
  })

  const fetchRows = useCallback(async () => {
    if (!businessId || !bookingId) return
    setLoading(true)
    try {
      const res = await listConsentFormsByBooking(businessId, bookingId)
      setRows(res?.data?.data || [])
    } catch (err) {
      // 靜默：可能未開通或無資料
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [businessId, bookingId])

  useEffect(() => {
    if (featureEnabled) fetchRows()
  }, [featureEnabled, fetchRows])

  /**
   * 發送前先讓店家挑要發哪幾份。
   *
   * 後端 sendForBooking 一直支援 templateIds，只是前端沒帶 —— 結果是
   * 一律發全部適用的範本，店家只想補寄其中一份也做不到。
   * 預設全勾＝維持原本的行為，不勾任何一份就退回讓後端自己判斷（等同原行為）。
   */
  const openSendPicker = async () => {
    setPicker({ open: true, loading: true, templates: [], selected: [] })
    try {
      const res = await listConsentFormTemplates(businessId, {
        formType: 'treatment'
      })
      const list = res?.data?.data || []
      setPicker({
        open: true,
        loading: false,
        templates: list,
        selected: list.map((t) => t._id)
      })
    } catch (err) {
      // 撈不到範本清單就退回原本的行為：直接送，讓後端依綁定判斷
      setPicker({ open: false, loading: false, templates: [], selected: [] })
      handleSend(null)
    }
  }

  const handleSend = async (templateIds) => {
    setSending(true)
    try {
      const res = await sendConsentForm(businessId, {
        bookingId,
        ...(templateIds && templateIds.length ? { templateIds } : {})
      })
      message.success(res?.data?.message || '已發送')
      setPicker((p) => ({ ...p, open: false }))
      fetchRows()
    } catch (err) {
      message.error(getApiErrorMessage(err, '發送失敗'))
    } finally {
      setSending(false)
    }
  }

  // 免登入簽署連結視窗：重發沒送到時自動跳出，也可以從列上的「連結」打開
  const [signLink, setSignLink] = useState({
    open: false,
    url: '',
    undelivered: false
  })

  const handleResend = async (row) => {
    try {
      const res = await resendConsentForm(businessId, row._id)
      const body = res?.data || {}
      // 後端照實回報有沒有送到；沒送到就直接給連結，不要讓店家以為發出去了
      if (body.delivered === false && body.signUrl) {
        setSignLink({ open: true, url: body.signUrl, undelivered: true })
      } else {
        message.success(body.message || '已重新發送')
      }
      fetchRows()
    } catch (err) {
      message.error(getApiErrorMessage(err, '重新發送失敗'))
    }
  }

  const openSignLink = async (row) => {
    try {
      const res = await getConsentSignLink(businessId, row._id)
      const url = res?.data?.data?.url
      if (!url) throw new Error('no url')
      setSignLink({ open: true, url, undelivered: false })
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得簽署連結失敗'))
    }
  }

  const openPrintable = (row) => {
    window.open(
      `${API_BASE_URL}${consentFormPrintableUrl(businessId, row._id)}`,
      '_blank'
    )
  }

  if (!featureEnabled) return null

  return (
    <Card>
      <Title>
        <FileProtectOutlined />
        施作同意書
        <span style={{ flex: 1 }} />
        <Space>
          <Button size='small' icon={<ReloadOutlined />} onClick={fetchRows} />
          <Button
            size='small'
            type='primary'
            icon={<SendOutlined />}
            loading={sending}
            onClick={openSendPicker}
          >
            發送同意書
          </Button>
        </Space>
      </Title>

      {loading ? (
        <Spin />
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description='尚未發送同意書（點右上「發送同意書」依綁定範本寄給客人）'
        />
      ) : (
        rows.map((r) => (
          <Row key={r._id}>
            <Info>
              <div className='name'>{r.templateTitle}</div>
              <div className='meta'>
                {r.serviceName ? `${r.serviceName}・` : ''}
                發送 {formatTz(r.sentAt, 'MM/DD HH:mm', '—')}
                {r.signedAt
                  ? `　簽署 ${formatTz(r.signedAt, 'MM/DD HH:mm')}`
                  : ''}
              </div>
            </Info>
            <Space>
              {statusTag(r.status)}
              {r.status === 'signed' && r.questionCount > 0 && (
                <Button
                  size='small'
                  icon={<SolutionOutlined />}
                  onClick={() => openAnswers(r)}
                >
                  作答
                </Button>
              )}
              {r.status === 'signed' && (
                <Button
                  size='small'
                  icon={<PrinterOutlined />}
                  onClick={() => openPrintable(r)}
                >
                  留存
                </Button>
              )}
              {r.status !== 'signed' && r.status !== 'canceled' && (
                <Button
                  size='small'
                  icon={<LinkOutlined />}
                  onClick={() => openSignLink(r)}
                >
                  連結
                </Button>
              )}
              {r.status !== 'signed' && r.status !== 'canceled' && (
                <Popconfirm
                  title='重新發送此同意書給客人？'
                  okText='重發'
                  cancelText='取消'
                  onConfirm={() => handleResend(r)}
                >
                  <Button size='small' icon={<SendOutlined />}>
                    重發
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Row>
        ))
      )}
      <Modal
        open={answerView.open}
        title={`問卷作答・${answerView.title}`}
        footer={null}
        onCancel={() => setAnswerView((prev) => ({ ...prev, open: false }))}
      >
        {answerView.loading ? (
          <Spin />
        ) : answerView.answers.length ? (
          <ConsentAnswerList answers={answerView.answers} />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='這份同意書沒有問卷作答'
          />
        )}
      </Modal>

      <ConsentSignLinkModal
        open={signLink.open}
        url={signLink.url}
        undelivered={signLink.undelivered}
        onClose={() => setSignLink((v) => ({ ...v, open: false }))}
      />
      {/* 要發哪幾份 —— 後端本來就收 templateIds，這裡只是把選擇權給店家 */}
      <Modal
        open={picker.open}
        title='要發送哪幾份同意書？'
        okText={`發送${
          picker.selected.length ? `（${picker.selected.length} 份）` : ''
        }`}
        cancelText='取消'
        confirmLoading={sending}
        okButtonProps={{ disabled: !picker.selected.length }}
        onOk={() => handleSend(picker.selected)}
        onCancel={() => setPicker((p) => ({ ...p, open: false }))}
      >
        {picker.loading ? (
          <Spin />
        ) : picker.templates.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='還沒有啟用中的施作同意書範本'
          />
        ) : (
          <Checkbox.Group
            value={picker.selected}
            onChange={(v) => setPicker((p) => ({ ...p, selected: v }))}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            {picker.templates.map((t) => (
              <Checkbox key={t._id} value={t._id}>
                {t.title}
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </Modal>
    </Card>
  )
}

export default ConsentFormBookingSection
